import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Award, Clock, Eye, Users } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { useLang } from "../i18n";

const HERO_IMG = "https://images.unsplash.com/photo-1546448935-9d0f92876a03?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";
const VALUE_ICONS = [Eye, Clock, Users];

export default function Partners() {
  const { t, ta, lang } = useLang();
  const missions = ta("partners.missions");
  const values = ta("partners.values");

  return (
    <div className="min-h-screen bg-sand" data-testid="partners-page">
      <Seo
        title={t("partners.meta_title")}
        description={t("partners.meta_desc")}
        path="/partenaires"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: t("partners.meta_title"),
          description: t("partners.meta_desc"),
          inLanguage: lang,
        }}
      />
      <Navbar />

      {/* ---------------- HERO ---------------- */}
      <section className="relative overflow-hidden" data-testid="partners-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt={t("partners.h1")} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-navy/75 to-sand" />
        </div>
        <div className="relative mx-auto max-w-4xl px-4 pb-28 pt-40 text-center sm:px-6 lg:pt-48">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="overline-label mb-4 !text-gold">{t("partners.overline")}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="font-serif text-4xl font-semibold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t("partners.h1")}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.35 }}
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg">
            {t("partners.subtitle")}
          </motion.p>
        </div>
      </section>

      {/* ---------------- MISSIONS ---------------- */}
      <section className="mx-auto max-w-5xl px-4 py-20 sm:px-6" data-testid="partners-missions">
        <div className="space-y-5">
          {missions.map((m, i) => (
            <Reveal key={i} delay={i * 0.06}>
              <div data-testid={`partners-mission-${i}`}
                className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-shadow hover:shadow-lg sm:flex-row sm:items-start sm:gap-8 sm:p-10">
                <span className="inline-flex w-fit shrink-0 items-center gap-2 rounded-full bg-azure-soft px-4 py-2 text-xs font-bold uppercase tracking-wider text-azure">
                  <Award size={14} /> {m.year}
                </span>
                <div>
                  <h2 className="font-serif text-xl font-semibold text-navy sm:text-2xl">{m.title}</h2>
                  <p className="mt-2.5 text-sm leading-relaxed text-navy/65 sm:text-base">{m.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- VALEURS ---------------- */}
      <section className="bg-white" data-testid="partners-values">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="grid gap-6 sm:grid-cols-3">
            {values.map((v, i) => {
              const Icon = VALUE_ICONS[i];
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <div className="h-full rounded-3xl border border-slate-100 bg-sand p-8 text-center">
                    <Icon size={24} className="mx-auto text-gold" />
                    <h3 className="mt-4 font-serif text-lg font-semibold text-navy">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-navy/60">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- CTA ---------------- */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-navy p-10 text-center sm:p-14" data-testid="partners-cta">
          <h2 className="font-serif text-2xl font-semibold text-white sm:text-3xl">{t("partners.cta_title")}</h2>
          <p className="mx-auto mt-3 max-w-md text-sm text-white/60">{t("partners.cta_text")}</p>
          <Link
            to="/reservation?devis=1&sujet=congres"
            data-testid="partners-cta-button"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-azure px-9 py-4 text-sm font-bold text-white transition-colors hover:bg-azure-hover"
          >
            {t("partners.cta_button")} <ArrowRight size={15} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
